// src/pages/Products/ProductOTAList.tsx

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Descriptions, Button, Space, message, Modal, Alert, Tag } from 'antd';
import {
  ArrowLeftOutlined,
  UploadOutlined,
  DeleteOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { getOTA, deleteOTA } from '@/api/ota';
import { getProduct } from '@/api/products';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore';
import type { OTAFirmware } from '@/types/ota';

export const ProductOTAList = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const developerId = getCurrentDeveloperId();

  const [refreshing, setRefreshing] = useState(false);

  // 查询产品信息
  const productQuery = useQuery({
    queryKey: ['product', developerId, productId],
    queryFn: () => {
      if (!productId) throw new Error('产品 ID 不能为空');
      return getProduct(productId);
    },
    enabled: !!productId && !!developerId,
    staleTime: 30000,
  });

  // 查询 OTA 固件信息
  const otaQuery = useQuery({
    queryKey: ['ota', developerId, productId],
    queryFn: async (): Promise<OTAFirmware | null> => {
      if (!productId) throw new Error('产品 ID 不能为空');
      try {
        return await getOTA(productId);
      } catch {
        // 404 表示暂无 OTA 固件
        return null;
      }
    },
    enabled: !!productId && !!developerId,
    staleTime: 0,
    gcTime: 0,
  });

  const product = productQuery.data;
  const ota = otaQuery.data;
  const loading = productQuery.isLoading || otaQuery.isLoading;
  const error = productQuery.error || otaQuery.error;

  // 删除 OTA Mutation
  const deleteMutation = useMutation({
    mutationFn: (otaId: string) => {
      if (!productId) throw new Error('产品 ID 不能为空');
      return deleteOTA(productId, otaId);
    },
    onSuccess: () => {
      message.success('固件删除成功');
      queryClient.invalidateQueries({ queryKey: ['ota', developerId, productId] });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : '删除失败，请检查权限或网络。';
      message.error(errorMessage);
    },
  });

  // 刷新列表
  const handleRefresh = async () => {
    setRefreshing(true);
    message.loading('正在刷新...', 0);
    await queryClient.invalidateQueries({ queryKey: ['ota', developerId, productId] });
    message.destroy();
    message.success('列表已刷新');
    setRefreshing(false);
  };

  // 返回产品详情
  const handleBack = () => navigate(`/products/${productId}`);

  // 跳转到上传/升级页面
  const handleUpload = () => {
    if (ota) {
      // 已有固件，跳转到升级页面
      navigate(`/products/${productId}/ota/upgrade`);
    } else {
      // 无固件，跳转到上传页面
      navigate(`/products/${productId}/ota/upload`);
    }
  };

  // 删除确认
  const handleDelete = () => {
    if (!ota) return;
    Modal.confirm({
      title: '确认删除固件',
      icon: <WarningOutlined className="text-orange-500" />,
      okType: 'danger',
      okText: '确认删除',
      cancelText: '取消',
      content: (
        <div className="space-y-2">
          <p>确定要删除固件 <span className="font-mono text-red-500">{ota.filename}</span> 吗？</p>
          <p className="text-red-500 font-medium">此操作不可恢复，请谨慎操作！</p>
        </div>
      ),
      onOk: () => deleteMutation.mutate(ota.id),
    });
  };

  // 格式化文件大小
  const formatFileSize = (size: number | null | undefined): string => {
    if (size == null) return '—';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message="加载失败" description="无法加载固件信息，请检查网络或稍后重试。" className="mb-8" />;

  return (
    <div className="w-full space-y-8">
      {/* 标题 + 操作区 - ✅ 升级固件和删除固件都在右上角 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <InfoCircleOutlined className="text-blue-500" />
            OTA 固件管理
          </h1>
          {product && (
            <p className="text-gray-600 mt-1 flex items-center gap-2">
              所属产品：
              <Tag color="blue" className="px-3 py-1">{product.name}</Tag>
            </p>
          )}
          <p className="text-gray-500 mt-1">
            {ota ? (
              <span className="font-medium text-green-600">已上传固件 v{ota.version}</span>
            ) : (
              <span className="font-medium text-gray-400">暂无固件</span>
            )}
          </p>
        </div>
        <Space size="middle" wrap>
          <Button
            onClick={handleBack}
            icon={<ArrowLeftOutlined />}
            className="hover:bg-gray-50 transition-colors"
          >
            返回产品详情
          </Button>
          <Button
            onClick={handleRefresh}
            icon={<ReloadOutlined />}
            disabled={refreshing || otaQuery.isFetching}
            loading={refreshing || otaQuery.isFetching}
            className="hover:bg-gray-50 transition-colors"
          >
            刷新
          </Button>
          {/* ✅ 升级固件按钮 */}
          <Button
            type="primary"
            onClick={handleUpload}
            icon={<UploadOutlined />}
            className="px-6 hover:shadow-md transition-shadow"
            disabled={!ota}  // 无固件时禁用升级
          >
            升级固件
          </Button>
          {/* ✅ 删除固件按钮 - 放在升级固件右边 */}
          <Button
            danger
            onClick={handleDelete}
            icon={<DeleteOutlined />}
            loading={deleteMutation.isPending}
            disabled={!ota}  // 无固件时禁用删除
            className="px-6 hover:shadow-md transition-shadow"
          >
            删除固件
          </Button>
        </Space>
      </div>

      {/* 重要提示 */}
      <Alert
        message="OTA 固件说明"
        description={
          <div className="space-y-1 text-gray-700">
            <p>• <strong className="text-blue-600">一个产品只能有一个 OTA 固件</strong>，上传新固件会自动覆盖旧版本。</p>
            <p>• <strong className="text-orange-600">版本号必须递增</strong>，新版本号必须严格大于当前版本（语义化版本 x.x.x）。</p>
            <p>• <strong className="text-red-600">文件大小限制 5MB</strong>，支持 .bin/.zip/.ota/.hex/.fw 格式。</p>
            <p>• <strong className="text-red-600">删除操作不可恢复</strong>，请确认无设备依赖后再操作。</p>
          </div>
        }
        type="info"
        showIcon
        className="rounded-lg shadow-sm"
        banner
      />

      <br />

      {/* 固件信息卡片 */}
      <Card
        className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300"
        style={{ padding: '24px' } as React.CSSProperties}
      >
        {ota ? (
          <Descriptions
            column={1}
            bordered={false}
            labelStyle={{ width: '160px', fontWeight: 600, color: '#667085' }}
            contentStyle={{ color: '#1D2129' }}
            style={{
              '--ant-descriptions-item-margin-bottom': '16px',
              fontSize: '15px'
            } as React.CSSProperties}
          >
            <Descriptions.Item label="固件 ID">
              <span className="font-mono">{ota.id}</span>
            </Descriptions.Item>
            <Descriptions.Item label="文件名称">
              <span className="font-mono text-gray-800">{ota.filename}</span>
            </Descriptions.Item>
            <Descriptions.Item label="固件版本">
              <Tag color="purple" className="text-sm px-3 py-1">v{ota.version}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="文件大小">
              {formatFileSize(ota.file_size)}
            </Descriptions.Item>
            <Descriptions.Item label="文件类型">
              <span className="font-mono text-gray-700">{ota.content_type}</span>
            </Descriptions.Item>
            {/* {ota.file_checksum && (
              <Descriptions.Item label="SHA256 校验和">
                <span className="font-mono text-gray-600 text-xs break-all">
                  {ota.file_checksum.substring(0, 32)}...
                </span>
              </Descriptions.Item>
            )} */}
            <Descriptions.Item label="上传时间">
              {new Date(ota.created_at).toLocaleString('zh-CN')}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {new Date(ota.updated_at).toLocaleString('zh-CN')}
            </Descriptions.Item>
            {ota.description && (
              <Descriptions.Item label="版本描述">
                <span className="text-gray-700">{ota.description}</span>
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          // 暂无固件
          <div className="text-center py-16 flex flex-col items-center justify-center">
            <div className="text-8xl text-gray-200 mb-6">📦</div>
            <p className="text-gray-500 text-lg mb-2">暂无 OTA 固件</p>
            {/* <p className="text-gray-400 text-sm mb-8">上传固件后，设备可通过 OTA 方式远程升级。</p> */}
            <br />
            <Button
              type="primary"
              icon={<UploadOutlined />}
              size="large"
              onClick={handleUpload}
              className="px-8 py-2 hover:shadow-md transition-shadow"
            >
              上传第一个固件
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};